import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PrimaryButton, SecondaryButton } from "../../globalComponents";
import { useAuth } from "../../context/auth.context";
import { useWhitelabel } from "../../context/whitelabel.context";
import { VerifSubDomain } from "../../utils/verifSubdomain";
import { clearStoredAuthData } from "../../utils/authStorage";

const LAST_OPENED_RECEIPT_KEY = '@LastOpenedReceiptCard:BetBuilder'

const toPositiveNumberOrFallback = (value, fallback) => {
    const parsed = Number(value)
    if (Number.isFinite(parsed) && parsed > 0) {
        return parsed
    }

    return fallback
}

const firstDefinedValue = (sources, keys) => {
    for (const source of sources) {
        if (!source || typeof source !== 'object') {
            continue
        }

        for (const key of keys) {
            if (source[key] !== undefined && source[key] !== null) {
                return source[key]
            }
        }
    }

    return undefined
}

const normalizePendingResponse = (response, fallbackPage, fallbackPageSize) => {
    const payload = response?.data
    let items = []

    if (Array.isArray(payload)) {
        items = payload
    }
    else if (Array.isArray(payload?.data)) {
        items = payload.data
    }
    else if (Array.isArray(payload?.items)) {
        items = payload.items
    }
    else if (Array.isArray(payload?.data?.items)) {
        items = payload.data.items
    }

    const sources = [
        payload,
        payload?.data,
        payload?.meta,
        payload?.data?.meta,
        payload?.pagination,
        payload?.data?.pagination
    ]

    const page = toPositiveNumberOrFallback(
        firstDefinedValue(sources, ['page', 'currentPage', 'pageNumber']),
        fallbackPage
    )
    const pageSize = toPositiveNumberOrFallback(
        firstDefinedValue(sources, ['pageSize', 'perPage', 'size', 'limit']),
        fallbackPageSize
    )

    const totalItemsValue = firstDefinedValue(sources, ['totalItems', 'totalCount', 'count', 'total'])
    const totalItems = Number.isFinite(Number(totalItemsValue))
        ? Math.max(0, Number(totalItemsValue))
        : 0

    const totalPagesValue = firstDefinedValue(sources, ['totalPages', 'pages'])
    const parsedTotalPages = Number(totalPagesValue)
    const totalPages = Number.isFinite(parsedTotalPages) && parsedTotalPages > 0
        ? parsedTotalPages
        : Math.max(1, Math.ceil(totalItems / pageSize) || 1)

    const hasNextRaw = firstDefinedValue(sources, ['hasNextPage', 'hasNext'])
    const hasNext = typeof hasNextRaw === 'boolean' ? hasNextRaw : page < totalPages

    return {
        items,
        pagination: {
            page,
            pageSize,
            totalItems,
            totalPages,
            hasNext
        }
    }
}

const getRequestId = (item) => (
    item?.id ||
    item?.requestId ||
    item?.verificationRequestId ||
    item?.uuid ||
    ''
)

const getUserLabel = (item) => (
    item?.userName ||
    item?.username ||
    item?.name ||
    item?.email ||
    item?.userEmail ||
    'Usuario sem identificacao'
)

const buildReceiptUrl = (imageUrl) => {
    if (!imageUrl) {
        return ''
    }

    const isAbsoluteUrl = /^https?:\/\//i.test(imageUrl)
    if (isAbsoluteUrl) {
        return imageUrl
    }

    const apiBaseUrl = (process.env.REACT_APP_API_URL || '').replace(/\/+$/, '')
    if (!apiBaseUrl) {
        return imageUrl
    }

    const normalizedPath = imageUrl.startsWith('/') ? imageUrl : `/${imageUrl}`
    return `${apiBaseUrl}${normalizedPath}`
}

const formatDateToDayMonthYear = (value) => {
    if (!value) {
        return '-'
    }

    const date = new Date(value)
    if (Number.isNaN(date.getTime())) {
        return '-'
    }

    return date.toLocaleDateString('pt-BR')
}

const getRequestDateLabel = (item) => {
    const rawDate = item?.createdAt || item?.requestDate || item?.created_on
    return formatDateToDayMonthYear(rawDate)
}

const AdminPanel = () => {
    const navigate = useNavigate()
    const {
        isAdmin,
        setAuthStatus,
        setVerifiedUser,
        setIsAdmin,
        SetAdminAccessMode,
        ClearAdminAccessMode,
        GetPendingVerificationRequests,
        GetVerificationRequests,
        ReviewVerificationRequest
    } = useAuth()
    const { clienteId, VerifySubDomain, logoVertical } = useWhitelabel()

    const [activeTab, setActiveTab] = useState('verify-users')
    const [verifyUsersSubTab, setVerifyUsersSubTab] = useState('pending-users')
    const [affiliateId, setAffiliateId] = useState('')
    const [isLoading, setIsLoading] = useState(false)
    const [reviewingRequestId, setReviewingRequestId] = useState('')
    const [lastOpenedReceiptId, setLastOpenedReceiptId] = useState(() => {
        if (typeof window === 'undefined') {
            return ''
        }

        return sessionStorage.getItem(LAST_OPENED_RECEIPT_KEY) || ''
    })
    const [pendingRequests, setPendingRequests] = useState([])
    const [page, setPage] = useState(1)
    const [pageSize, setPageSize] = useState(10)
    const [searchInput, setSearchInput] = useState('')
    const [searchQuery, setSearchQuery] = useState('')
    const [pagination, setPagination] = useState({
        page: 1,
        pageSize: 10,
        totalItems: 0,
        totalPages: 1,
        hasNext: false
    })
    const [rejectionReasons, setRejectionReasons] = useState({})
    const [error, setError] = useState('')
    const [success, setSuccess] = useState('')
    const [allRequestsLoading, setAllRequestsLoading] = useState(false)
    const [allRequests, setAllRequests] = useState([])
    const [allPage, setAllPage] = useState(1)
    const [allPageSize, setAllPageSize] = useState(10)
    const [allSearchInput, setAllSearchInput] = useState('')
    const [allSearchQuery, setAllSearchQuery] = useState('')
    const [allStatus, setAllStatus] = useState('ALL')
    const [isMobileViewport, setIsMobileViewport] = useState(() => {
        if (typeof window === 'undefined') {
            return false
        }

        return window.innerWidth <= 768
    })
    const [allPagination, setAllPagination] = useState({
        page: 1,
        pageSize: 10,
        totalItems: 0,
        totalPages: 1,
        hasNext: false
    })
    const [allRequestsError, setAllRequestsError] = useState('')

    useEffect(() => {
        const updateViewport = () => {
            setIsMobileViewport(window.innerWidth <= 768)
        }

        updateViewport()
        window.addEventListener('resize', updateViewport)

        return () => {
            window.removeEventListener('resize', updateViewport)
        }
    }, [])

    const panelContainerStyle = {
        minHeight: '100vh',
        width: '100%',
        background: 'linear-gradient(140deg, #090909 0%, #111111 50%, #161616 100%)',
        color: '#fff',
        display: 'flex',
        justifyContent: 'center',
        padding: isMobileViewport ? 12 : 20
    }

    const panelContentStyle = {
        width: '100%',
        maxWidth: 980,
        display: 'flex',
        flexDirection: 'column',
        gap: isMobileViewport ? 14 : 20
    }

    const topCardStyle = {
        border: '1px solid #2c2c2c',
        backgroundColor: '#0d0d0d',
        borderRadius: 16,
        padding: isMobileViewport ? 14 : 20,
        display: 'flex',
        flexWrap: 'wrap',
        gap: 14,
        alignItems: isMobileViewport ? 'stretch' : 'center',
        justifyContent: 'space-between'
    }

    const tabBarStyle = {
        display: 'flex',
        borderBottom: '1px solid #2c2c2c',
        overflowX: 'auto',
        scrollbarWidth: 'thin'
    }

    const sectionContentStyle = {
        padding: isMobileViewport ? 14 : 20,
        display: 'flex',
        flexDirection: 'column',
        gap: 14
    }

    const resolveAffiliateId = async () => {
        const fromContext = Number(clienteId)
        if (Number.isFinite(fromContext) && fromContext > 0) {
            return fromContext
        }

        const fromStorage = Number(localStorage.getItem('@ClientId:BetBuilder'))
        if (Number.isFinite(fromStorage) && fromStorage > 0) {
            return fromStorage
        }

        const subdomain = VerifSubDomain()
        await VerifySubDomain(subdomain)

        const refreshed = Number(localStorage.getItem('@ClientId:BetBuilder'))
        if (Number.isFinite(refreshed) && refreshed > 0) {
            return refreshed
        }

        throw new Error('Nao foi possivel identificar o affiliateId do afiliado atual.')
    }

    const loadPendingRequests = async (options = {}) => {
        const nextPage = toPositiveNumberOrFallback(options.page, page)
        const nextPageSize = toPositiveNumberOrFallback(options.pageSize, pageSize)
        const nextSearch = typeof options.search === 'string' ? options.search : searchQuery

        setIsLoading(true)
        setError('')
        setSuccess('')

        try {
            const resolvedAffiliateId = await resolveAffiliateId()
            setAffiliateId(String(resolvedAffiliateId))
            const response = await GetPendingVerificationRequests(resolvedAffiliateId, {
                page: nextPage,
                pageSize: nextPageSize,
                search: nextSearch
            })

            const normalized = normalizePendingResponse(response, nextPage, nextPageSize)

            setPendingRequests(normalized.items)
            setPagination(normalized.pagination)
            setPage(normalized.pagination.page)
            setPageSize(normalized.pagination.pageSize)
            setSearchQuery(nextSearch)
        } catch (err) {
            const apiMessage = err?.response?.data?.error?.message || err?.response?.data?.message
            setError(apiMessage || 'Nao foi possivel carregar verificacoes pendentes.')
        } finally {
            setIsLoading(false)
        }
    }

    const loadVerificationRequests = async (options = {}) => {
        const nextPage = toPositiveNumberOrFallback(options.page, allPage)
        const nextPageSize = toPositiveNumberOrFallback(options.pageSize, allPageSize)
        const nextSearch = typeof options.search === 'string' ? options.search : allSearchQuery
        const nextStatus = typeof options.status === 'string' ? options.status : allStatus

        setAllRequestsLoading(true)
        setAllRequestsError('')

        try {
            const resolvedAffiliateId = await resolveAffiliateId()
            setAffiliateId(String(resolvedAffiliateId))

            const payload = {
                page: nextPage,
                pageSize: nextPageSize,
                search: nextSearch
            }

            if (nextStatus && nextStatus !== 'ALL') {
                payload.status = nextStatus
            }

            const response = await GetVerificationRequests(resolvedAffiliateId, payload)

            const normalized = normalizePendingResponse(response, nextPage, nextPageSize)

            setAllRequests(normalized.items)
            setAllPagination(normalized.pagination)
            setAllPage(normalized.pagination.page)
            setAllPageSize(normalized.pagination.pageSize)
            setAllSearchQuery(nextSearch)
            setAllStatus(nextStatus)
        } catch (err) {
            const apiMessage = err?.response?.data?.error?.message || err?.response?.data?.message
            setAllRequestsError(apiMessage || 'Nao foi possivel carregar a lista de verificacoes.')
        } finally {
            setAllRequestsLoading(false)
        }
    }

    const handleReview = async (item, approve) => {
        const requestId = getRequestId(item)
        if (!requestId) {
            setError('Solicitacao sem identificador valido.')
            return
        }

        const rejectionReason = (rejectionReasons[requestId] || '').trim()

        if (!approve && !rejectionReason) {
            setError('Informe o motivo da reprovacao para continuar.')
            return
        }

        setReviewingRequestId(requestId)
        setError('')
        setSuccess('')

        try {
            await ReviewVerificationRequest(requestId, approve, rejectionReason)
            setSuccess(approve ? 'Usuario aprovado com sucesso.' : 'Usuario reprovado com sucesso.')
            await loadPendingRequests({ page, pageSize, search: searchQuery })
        } catch (err) {
            const apiMessage = err?.response?.data?.error?.message || err?.response?.data?.message
            setError(apiMessage || 'Nao foi possivel revisar a solicitacao.')
        } finally {
            setReviewingRequestId('')
        }
    }

    const handleOpenReceipt = (requestId) => {
        if (!requestId) {
            return
        }

        setLastOpenedReceiptId(requestId)
        sessionStorage.setItem(LAST_OPENED_RECEIPT_KEY, requestId)
    }

    const handleLogout = () => {
        clearStoredAuthData()
        sessionStorage.removeItem('@Token:BetBuilder')
        localStorage.removeItem('@Email:BetBuilder')
        localStorage.removeItem('@Nome:BetBuilder')
        localStorage.removeItem('@Telefone:BetBuilder')
        localStorage.removeItem('saldo')
        localStorage.removeItem('horarioLogin')

        setAuthStatus(false)
        setVerifiedUser(null)
        setIsAdmin(false)
        ClearAdminAccessMode()
        navigate('/Login')
    }

    const handleSwitchToSite = () => {
        SetAdminAccessMode('site')
        navigate('/')
    }

    const handleSearchSubmit = async (event) => {
        event.preventDefault()
        await loadPendingRequests({ page: 1, pageSize, search: searchInput.trim() })
    }

    const handleClearSearch = async () => {
        setSearchInput('')
        await loadPendingRequests({ page: 1, pageSize, search: '' })
    }

    const handlePageSizeChange = async (event) => {
        const nextPageSize = toPositiveNumberOrFallback(event.target.value, pageSize)
        await loadPendingRequests({ page: 1, pageSize: nextPageSize, search: searchQuery })
    }

    const handlePreviousPage = async () => {
        const previousPage = Math.max(1, page - 1)
        await loadPendingRequests({ page: previousPage, pageSize, search: searchQuery })
    }

    const handleNextPage = async () => {
        const nextPage = Math.min(pagination.totalPages || 1, page + 1)
        await loadPendingRequests({ page: nextPage, pageSize, search: searchQuery })
    }

    const handleAllSearchSubmit = async (event) => {
        event.preventDefault()
        await loadVerificationRequests({ page: 1, pageSize: allPageSize, search: allSearchInput.trim(), status: allStatus })
    }

    const handleAllClearSearch = async () => {
        setAllSearchInput('')
        await loadVerificationRequests({ page: 1, pageSize: allPageSize, search: '', status: allStatus })
    }

    const handleAllPageSizeChange = async (event) => {
        const nextPageSize = toPositiveNumberOrFallback(event.target.value, allPageSize)
        await loadVerificationRequests({ page: 1, pageSize: nextPageSize, search: allSearchQuery, status: allStatus })
    }

    const handleAllStatusChange = async (event) => {
        const nextStatus = event.target.value
        setAllStatus(nextStatus)
        await loadVerificationRequests({ page: 1, pageSize: allPageSize, search: allSearchQuery, status: nextStatus })
    }

    const handleAllPreviousPage = async () => {
        const previousPage = Math.max(1, allPage - 1)
        await loadVerificationRequests({ page: previousPage, pageSize: allPageSize, search: allSearchQuery, status: allStatus })
    }

    const handleAllNextPage = async () => {
        const nextPage = Math.min(allPagination.totalPages || 1, allPage + 1)
        await loadVerificationRequests({ page: nextPage, pageSize: allPageSize, search: allSearchQuery, status: allStatus })
    }

    useEffect(() => {
        if (!isAdmin) {
            navigate('/')
            return
        }

        loadPendingRequests()
    }, [isAdmin])

    useEffect(() => {
        if (!isAdmin || activeTab !== 'verify-users' || verifyUsersSubTab !== 'all-users') {
            return
        }

        loadVerificationRequests()
    }, [activeTab, verifyUsersSubTab, isAdmin])

    return (
        <div style={panelContainerStyle}>
            <div style={panelContentStyle}>
                <div style={topCardStyle}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 14, width: isMobileViewport ? '100%' : 'auto' }}>
                        <img
                            src={logoVertical}
                            alt="Logo"
                            style={{ width: isMobileViewport ? 40 : 48, height: isMobileViewport ? 40 : 48, objectFit: 'contain' }}
                        />
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
                            <h1 style={{ margin: 0, fontSize: isMobileViewport ? 20 : 26 }}>Painel de Controle</h1>
                            <span style={{ color: '#cfcfcf' }}>AffiliateId atual: {affiliateId || '-'}</span>
                        </div>
                    </div>

                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', width: isMobileViewport ? '100%' : 'auto' }}>
                        <SecondaryButton
                            onClick={handleSwitchToSite}
                            style={{ minWidth: isMobileViewport ? 0 : 190, flex: isMobileViewport ? 1 : 'initial', minHeight: 44 }}
                        >
                            Entrar no Site
                        </SecondaryButton>
                        <button
                            onClick={handleLogout}
                            style={{
                                minWidth: isMobileViewport ? 0 : 150,
                                flex: isMobileViewport ? 1 : 'initial',
                                height: 44,
                                borderRadius: 48,
                                border: '1px solid #3d3d3d',
                                backgroundColor: 'transparent',
                                color: '#d8d8d8',
                                cursor: 'pointer'
                            }}
                        >
                            Sair
                        </button>
                    </div>
                </div>

                <div style={{
                    border: '1px solid #2c2c2c',
                    backgroundColor: '#0d0d0d',
                    borderRadius: 16,
                    overflow: 'hidden'
                }}>
                    <div style={tabBarStyle}>
                        <button
                            onClick={() => setActiveTab('verify-users')}
                            style={{
                                border: 'none',
                                background: activeTab === 'verify-users' ? 'var(--Primary-linear)' : 'transparent',
                                color: '#fff',
                                fontSize: isMobileViewport ? 14 : 15,
                                padding: isMobileViewport ? '12px 16px' : '12px 18px',
                                minHeight: 44,
                                whiteSpace: 'nowrap',
                                cursor: 'pointer'
                            }}
                        >
                            Verificar Usuarios
                        </button>
                    </div>

                    <div style={sectionContentStyle}>
                        {activeTab === 'verify-users' && (
                            <>
                                <div style={tabBarStyle}>
                                    <button
                                        onClick={() => setVerifyUsersSubTab('pending-users')}
                                        style={{
                                            border: 'none',
                                            background: verifyUsersSubTab === 'pending-users' ? 'var(--Primary-linear)' : 'transparent',
                                            color: '#fff',
                                            fontSize: 14,
                                            padding: isMobileViewport ? '11px 14px' : '10px 14px',
                                            minHeight: 42,
                                            whiteSpace: 'nowrap',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        Usuarios Pendentes
                                    </button>
                                    <button
                                        onClick={() => setVerifyUsersSubTab('all-users')}
                                        style={{
                                            border: 'none',
                                            background: verifyUsersSubTab === 'all-users' ? 'var(--Primary-linear)' : 'transparent',
                                            color: '#fff',
                                            fontSize: 14,
                                            padding: isMobileViewport ? '11px 14px' : '10px 14px',
                                            minHeight: 42,
                                            whiteSpace: 'nowrap',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        Lista Completa
                                    </button>
                                </div>

                                <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: 10 }}>
                                    <h2 style={{ margin: 0, fontSize: isMobileViewport ? 19 : 22 }}>
                                        {verifyUsersSubTab === 'pending-users' ? 'Usuarios Pendentes' : 'Lista Completa'}
                                    </h2>
                                    <PrimaryButton
                                        onClick={() => {
                                            if (verifyUsersSubTab === 'pending-users') {
                                                loadPendingRequests({ page, pageSize, search: searchQuery })
                                                return
                                            }

                                            loadVerificationRequests({
                                                page: allPage,
                                                pageSize: allPageSize,
                                                search: allSearchQuery,
                                                status: allStatus
                                            })
                                        }}
                                        style={{ minWidth: isMobileViewport ? 0 : 160, width: isMobileViewport ? '100%' : 'auto', minHeight: 44 }}
                                    >
                                        Atualizar lista
                                    </PrimaryButton>
                                </div>

                                {verifyUsersSubTab === 'pending-users' && (
                                <>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
                                    <form
                                        onSubmit={handleSearchSubmit}
                                        style={{
                                            display: 'flex',
                                            gap: 10,
                                            flexWrap: 'wrap',
                                            flex: 1,
                                            width: isMobileViewport ? '100%' : 'auto'
                                        }}
                                    >
                                        <input
                                            type="text"
                                            value={searchInput}
                                            onChange={(event) => setSearchInput(event.target.value)}
                                            placeholder="Pesquisar por nome, email ou usuario"
                                            style={{
                                                minWidth: isMobileViewport ? 0 : 240,
                                                flex: 1,
                                                width: isMobileViewport ? '100%' : 'auto',
                                                maxWidth: isMobileViewport ? '100%' : 460,
                                                borderRadius: 8,
                                                border: '1px solid #3a3a3a',
                                                backgroundColor: '#0f0f0f',
                                                color: '#fff',
                                                padding: '10px 12px',
                                                minHeight: 42
                                            }}
                                        />
                                        <PrimaryButton
                                            type="submit"
                                            style={{ minWidth: isMobileViewport ? 0 : 120, flex: isMobileViewport ? 1 : 'initial', minHeight: 42 }}
                                        >
                                            Buscar
                                        </PrimaryButton>
                                        <SecondaryButton
                                            type="button"
                                            onClick={handleClearSearch}
                                            style={{ minWidth: isMobileViewport ? 0 : 120, flex: isMobileViewport ? 1 : 'initial', minHeight: 42 }}
                                        >
                                            Limpar
                                        </SecondaryButton>
                                    </form>

                                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', width: isMobileViewport ? '100%' : 'auto' }}>
                                        <label htmlFor="pending-page-size" style={{ color: '#cfcfcf', fontSize: 14 }}>
                                            Itens por pagina
                                        </label>
                                        <select
                                            id="pending-page-size"
                                            value={pageSize}
                                            onChange={handlePageSizeChange}
                                            style={{
                                                borderRadius: 8,
                                                border: '1px solid #3a3a3a',
                                                backgroundColor: '#0f0f0f',
                                                color: '#fff',
                                                padding: '8px 10px',
                                                minHeight: 40,
                                                width: isMobileViewport ? '100%' : 'auto'
                                            }}
                                        >
                                            <option value={10}>10</option>
                                            <option value={20}>20</option>
                                            <option value={50}>50</option>
                                        </select>
                                    </div>
                                </div>

                                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', color: '#cfcfcf', fontSize: isMobileViewport ? 13 : 14 }}>
                                    <span>
                                        Total: {pagination.totalItems} | Pagina {page} de {pagination.totalPages}
                                    </span>
                                    <span>
                                        Filtro atual: {searchQuery ? `"${searchQuery}"` : 'sem filtro'}
                                    </span>
                                </div>

                                {isLoading && <span style={{ color: '#c9c9c9' }}>Carregando solicitacoes pendentes...</span>}
                                {!isLoading && error && <span style={{ color: '#ff8b8b' }}>{error}</span>}
                                {!isLoading && success && <span style={{ color: '#89ffb0' }}>{success}</span>}

                                {!isLoading && pendingRequests.length === 0 && (
                                    <div style={{
                                        border: '1px dashed #3b3b3b',
                                        borderRadius: 10,
                                        padding: 14,
                                        color: '#c8c8c8'
                                    }}>
                                        Nenhuma solicitacao pendente para este afiliado.
                                    </div>
                                )}

                                {!isLoading && pendingRequests.map((item, index) => {
                                    const requestId = getRequestId(item)
                                    const receiptUrl = buildReceiptUrl(item?.imageUrl)
                                    const isReviewingCurrent = reviewingRequestId === requestId

                                    return (
                                        <div key={requestId || `item-${index}`} style={{
                                            border: lastOpenedReceiptId === requestId ? '1px solid #5ea3ff' : '1px solid #303030',
                                            borderRadius: 12,
                                            padding: 14,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 10,
                                            backgroundColor: lastOpenedReceiptId === requestId ? '#162234' : '#121212',
                                            boxShadow: lastOpenedReceiptId === requestId
                                                ? '0 0 0 1px rgba(94,163,255,0.25), 0 8px 24px rgba(25,56,97,0.35)'
                                                : 'none',
                                            transition: 'background-color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease'
                                        }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                                                <strong>{getUserLabel(item)}</strong>
                                                <span style={{ color: '#cfcfcf' }}>ID: {requestId || '-'}</span>
                                            </div>

                                            <div style={{ color: '#bcbcbc', fontSize: 14 }}>
                                                <span>
                                                    Data: {getRequestDateLabel(item)}
                                                </span>
                                            </div>

                                            {item?.imageUrl && (
                                                <a
                                                    href={receiptUrl}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    onClick={() => handleOpenReceipt(requestId)}
                                                    style={{ color: '#9bc8ff' }}
                                                >
                                                    Abrir comprovante enviado
                                                </a>
                                            )}

                                            {lastOpenedReceiptId === requestId && (
                                                <span style={{ color: '#8cc2ff', fontSize: 13 }}>
                                                    Ultimo comprovante aberto
                                                </span>
                                            )}

                                            <textarea
                                                rows={3}
                                                value={rejectionReasons[requestId] || ''}
                                                onChange={(event) => {
                                                    setRejectionReasons((current) => ({
                                                        ...current,
                                                        [requestId]: event.target.value
                                                    }))
                                                }}
                                                placeholder="Motivo da reprovacao (obrigatorio para reprovar)"
                                                style={{
                                                    borderRadius: 8,
                                                    border: '1px solid #3a3a3a',
                                                    backgroundColor: '#0f0f0f',
                                                    color: '#fff',
                                                    padding: 10,
                                                    resize: 'vertical'
                                                }}
                                            />

                                            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                                                <PrimaryButton
                                                    onClick={() => handleReview(item, true)}
                                                    disabled={isReviewingCurrent}
                                                    style={{
                                                        minWidth: isMobileViewport ? 0 : 130,
                                                        flex: isMobileViewport ? 1 : 'initial',
                                                        minHeight: 42,
                                                        opacity: isReviewingCurrent ? 0.65 : 1
                                                    }}
                                                >
                                                    Aprovar
                                                </PrimaryButton>
                                                <SecondaryButton
                                                    onClick={() => handleReview(item, false)}
                                                    disabled={isReviewingCurrent}
                                                    style={{
                                                        minWidth: isMobileViewport ? 0 : 130,
                                                        flex: isMobileViewport ? 1 : 'initial',
                                                        minHeight: 42,
                                                        opacity: isReviewingCurrent ? 0.65 : 1
                                                    }}
                                                >
                                                    Reprovar
                                                </SecondaryButton>
                                            </div>
                                        </div>
                                    )
                                })}

                                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, flexWrap: 'wrap' }}>
                                    <SecondaryButton
                                        type="button"
                                        onClick={handlePreviousPage}
                                        disabled={isLoading || page <= 1}
                                        style={{
                                            minWidth: isMobileViewport ? 0 : 130,
                                            flex: isMobileViewport ? 1 : 'initial',
                                            minHeight: 42,
                                            opacity: isLoading || page <= 1 ? 0.65 : 1
                                        }}
                                    >
                                        Pagina anterior
                                    </SecondaryButton>
                                    <PrimaryButton
                                        type="button"
                                        onClick={handleNextPage}
                                        disabled={isLoading || (!pagination.hasNext && page >= pagination.totalPages)}
                                        style={{
                                            minWidth: isMobileViewport ? 0 : 130,
                                            flex: isMobileViewport ? 1 : 'initial',
                                            minHeight: 42,
                                            opacity: isLoading || (!pagination.hasNext && page >= pagination.totalPages) ? 0.65 : 1
                                        }}
                                    >
                                        Proxima pagina
                                    </PrimaryButton>
                                </div>
                                </>
                                )}

                                {verifyUsersSubTab === 'all-users' && (
                                <>

                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
                                    <form
                                        onSubmit={handleAllSearchSubmit}
                                        style={{
                                            display: 'flex',
                                            gap: 10,
                                            flexWrap: 'wrap',
                                            flex: 1,
                                            width: isMobileViewport ? '100%' : 'auto'
                                        }}
                                    >
                                        <input
                                            type="text"
                                            value={allSearchInput}
                                            onChange={(event) => setAllSearchInput(event.target.value)}
                                            placeholder="Pesquisar por nome, email ou usuario"
                                            style={{
                                                minWidth: isMobileViewport ? 0 : 240,
                                                flex: 1,
                                                width: isMobileViewport ? '100%' : 'auto',
                                                maxWidth: isMobileViewport ? '100%' : 460,
                                                borderRadius: 8,
                                                border: '1px solid #3a3a3a',
                                                backgroundColor: '#0f0f0f',
                                                color: '#fff',
                                                padding: '10px 12px',
                                                minHeight: 42
                                            }}
                                        />
                                        <PrimaryButton
                                            type="submit"
                                            style={{ minWidth: isMobileViewport ? 0 : 120, flex: isMobileViewport ? 1 : 'initial', minHeight: 42 }}
                                        >
                                            Buscar
                                        </PrimaryButton>
                                        <SecondaryButton
                                            type="button"
                                            onClick={handleAllClearSearch}
                                            style={{ minWidth: isMobileViewport ? 0 : 120, flex: isMobileViewport ? 1 : 'initial', minHeight: 42 }}
                                        >
                                            Limpar
                                        </SecondaryButton>
                                    </form>

                                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', width: isMobileViewport ? '100%' : 'auto' }}>
                                        <label htmlFor="all-status" style={{ color: '#cfcfcf', fontSize: 14 }}>
                                            Status
                                        </label>
                                        <select
                                            id="all-status"
                                            value={allStatus}
                                            onChange={handleAllStatusChange}
                                            style={{
                                                borderRadius: 8,
                                                border: '1px solid #3a3a3a',
                                                backgroundColor: '#0f0f0f',
                                                color: '#fff',
                                                padding: '8px 10px',
                                                minHeight: 40,
                                                width: isMobileViewport ? '100%' : 'auto'
                                            }}
                                        >
                                            <option value="ALL">Todos</option>
                                            <option value="PENDING">PENDENTE</option>
                                            <option value="APPROVED">APROVADO</option>
                                            <option value="REJECTED">REJEITADO</option>
                                        </select>
                                    </div>

                                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', width: isMobileViewport ? '100%' : 'auto' }}>
                                        <label htmlFor="all-page-size" style={{ color: '#cfcfcf', fontSize: 14 }}>
                                            Itens por pagina
                                        </label>
                                        <select
                                            id="all-page-size"
                                            value={allPageSize}
                                            onChange={handleAllPageSizeChange}
                                            style={{
                                                borderRadius: 8,
                                                border: '1px solid #3a3a3a',
                                                backgroundColor: '#0f0f0f',
                                                color: '#fff',
                                                padding: '8px 10px',
                                                minHeight: 40,
                                                width: isMobileViewport ? '100%' : 'auto'
                                            }}
                                        >
                                            <option value={10}>10</option>
                                            <option value={20}>20</option>
                                            <option value={50}>50</option>
                                        </select>
                                    </div>
                                </div>

                                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', color: '#cfcfcf', fontSize: isMobileViewport ? 13 : 14 }}>
                                    <span>
                                        Total: {allPagination.totalItems} | Pagina {allPage} de {allPagination.totalPages}
                                    </span>
                                    <span>
                                        Filtros: {allStatus === 'ALL' ? 'todos os status' : allStatus} | Busca: {allSearchQuery ? `"${allSearchQuery}"` : 'sem filtro'}
                                    </span>
                                </div>

                                {allRequestsLoading && <span style={{ color: '#c9c9c9' }}>Carregando lista completa...</span>}
                                {!allRequestsLoading && allRequestsError && <span style={{ color: '#ff8b8b' }}>{allRequestsError}</span>}

                                {!allRequestsLoading && allRequests.length === 0 && (
                                    <div style={{
                                        border: '1px dashed #3b3b3b',
                                        borderRadius: 10,
                                        padding: 14,
                                        color: '#c8c8c8'
                                    }}>
                                        Nenhuma solicitacao encontrada para os filtros aplicados.
                                    </div>
                                )}

                                {!allRequestsLoading && allRequests.length > 0 && (
                                    <div style={{
                                        border: '1px solid #2f2f2f',
                                        borderRadius: 12,
                                        overflow: 'hidden',
                                        backgroundColor: '#121212'
                                    }}>
                                        <div style={{
                                            width: '100%',
                                            overflowX: 'scroll',
                                            overflowY: 'hidden',
                                            WebkitOverflowScrolling: 'touch',
                                            scrollbarWidth: 'thin'
                                        }}>
                                            <table style={{
                                                width: '100%',
                                                minWidth: 1200,
                                                borderCollapse: 'separate',
                                                borderSpacing: 0,
                                                color: '#fff'
                                            }}>
                                                <thead>
                                                    <tr>
                                                        <th style={{
                                                            position: 'sticky',
                                                            left: 0,
                                                            zIndex: 3,
                                                            backgroundColor: '#1b1b1b',
                                                            textAlign: 'left',
                                                            padding: '12px 14px',
                                                            borderBottom: '1px solid #323232',
                                                            minWidth: 200
                                                        }}>
                                                            Usuario
                                                        </th>
                                                        <th style={{ textAlign: 'left', padding: '12px 14px', borderBottom: '1px solid #323232', whiteSpace: 'nowrap' }}>ID</th>
                                                        <th style={{ textAlign: 'left', padding: '12px 14px', borderBottom: '1px solid #323232', whiteSpace: 'nowrap' }}>Status</th>
                                                        <th style={{ textAlign: 'left', padding: '12px 14px', borderBottom: '1px solid #323232', whiteSpace: 'nowrap' }}>Data</th>
                                                        <th style={{ textAlign: 'left', padding: '12px 14px', borderBottom: '1px solid #323232' }}>Motivo da reprovacao</th>
                                                        <th style={{ textAlign: 'left', padding: '12px 14px', borderBottom: '1px solid #323232', whiteSpace: 'nowrap' }}>Comprovante</th>
                                                    </tr>
                                                </thead>

                                                <tbody>
                                                    {allRequests.map((item, index) => {
                                                        const requestId = getRequestId(item)
                                                        const receiptUrl = buildReceiptUrl(item?.imageUrl)
                                                        const statusLabel = item?.status || 'UNKNOWN'
                                                        const isHighlighted = lastOpenedReceiptId === requestId
                                                        const rowBg = isHighlighted ? '#162234' : '#121212'

                                                        return (
                                                            <tr key={requestId || `all-item-${index}`} style={{ backgroundColor: rowBg }}>
                                                                <td style={{
                                                                    position: 'sticky',
                                                                    left: 0,
                                                                    zIndex: 2,
                                                                    backgroundColor: rowBg,
                                                                    padding: '12px 14px',
                                                                    borderBottom: '1px solid #292929',
                                                                    fontWeight: 600,
                                                                    minWidth: 200,
                                                                    boxShadow: '1px 0 0 #292929'
                                                                }}>
                                                                    {getUserLabel(item)}
                                                                </td>
                                                                <td style={{ padding: '12px 14px', borderBottom: '1px solid #292929', color: '#cfcfcf', whiteSpace: 'nowrap' }}>
                                                                    {requestId || '-'}
                                                                </td>
                                                                <td style={{ padding: '12px 14px', borderBottom: '1px solid #292929', whiteSpace: 'nowrap' }}>
                                                                    {statusLabel}
                                                                </td>
                                                                <td style={{ padding: '12px 14px', borderBottom: '1px solid #292929', whiteSpace: 'nowrap', color: '#cfcfcf' }}>
                                                                    {getRequestDateLabel(item)}
                                                                </td>
                                                                <td style={{ padding: '12px 14px', borderBottom: '1px solid #292929', color: item?.rejectionReason ? '#ffc2c2' : '#9b9b9b' }}>
                                                                    {item?.rejectionReason || '-'}
                                                                </td>
                                                                <td style={{ padding: '12px 14px', borderBottom: '1px solid #292929', whiteSpace: 'nowrap' }}>
                                                                    {item?.imageUrl ? (
                                                                        <>
                                                                            <a
                                                                                href={receiptUrl}
                                                                                target="_blank"
                                                                                rel="noreferrer"
                                                                                onClick={() => handleOpenReceipt(requestId)}
                                                                                style={{ color: '#9bc8ff' }}
                                                                            >
                                                                                Abrir comprovante enviado
                                                                            </a>
                                                                            {isHighlighted && (
                                                                                <span style={{ color: '#8cc2ff', fontSize: 12, display: 'block', marginTop: 6 }}>
                                                                                    Ultimo comprovante aberto
                                                                                </span>
                                                                            )}
                                                                        </>
                                                                    ) : (
                                                                        <span style={{ color: '#9b9b9b' }}>-</span>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        )
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}

                                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, flexWrap: 'wrap' }}>
                                    <SecondaryButton
                                        type="button"
                                        onClick={handleAllPreviousPage}
                                        disabled={allRequestsLoading || allPage <= 1}
                                        style={{
                                            minWidth: isMobileViewport ? 0 : 130,
                                            flex: isMobileViewport ? 1 : 'initial',
                                            minHeight: 42,
                                            opacity: allRequestsLoading || allPage <= 1 ? 0.65 : 1
                                        }}
                                    >
                                        Pagina anterior
                                    </SecondaryButton>
                                    <PrimaryButton
                                        type="button"
                                        onClick={handleAllNextPage}
                                        disabled={allRequestsLoading || (!allPagination.hasNext && allPage >= allPagination.totalPages)}
                                        style={{
                                            minWidth: isMobileViewport ? 0 : 130,
                                            flex: isMobileViewport ? 1 : 'initial',
                                            minHeight: 42,
                                            opacity: allRequestsLoading || (!allPagination.hasNext && allPage >= allPagination.totalPages) ? 0.65 : 1
                                        }}
                                    >
                                        Proxima pagina
                                    </PrimaryButton>
                                </div>
                                </>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    )
}

export default AdminPanel
